"use client"

import * as React from "react"
import { Search, FileText, Code, Home, Settings, X } from "lucide-react"
import { useRouter } from "next/navigation"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog"
import { Input } from "../ui/input"
import { ScrollArea } from "../ui/scroll-area"
import { cn } from "../lib/utils"

export interface SearchItem {
    id: string
    title: string
    description?: string
    url: string
    category?: string
    icon?: React.ReactNode
}

export interface SearchDialogProps {
    /** Search items to display */
    items?: SearchItem[]
    /** Placeholder text for search input */
    placeholder?: string
    /** Custom search function (if not provided, uses client-side filtering) */
    onSearch?: (query: string) => Promise<SearchItem[]> | SearchItem[]
    /** Callback when an item is selected */
    onSelect?: (item: SearchItem) => void
    /** Custom empty state message */
    emptyMessage?: string
    /** Whether to show category labels */
    showCategories?: boolean
    /** When false, only CMD+K opens the dialog (no sidebar trigger button). Use for global search on pages without sidebar. */
    showTrigger?: boolean
}

export function SearchDialog({
    items = [],
    placeholder = "Search...",
    onSearch,
    onSelect,
    emptyMessage = "No results found.",
    showCategories = true,
    showTrigger = true,
}: SearchDialogProps) {
    const [open, setOpen] = React.useState(false)
    const [query, setQuery] = React.useState("")
    const [results, setResults] = React.useState<SearchItem[]>(items)
    const [loading, setLoading] = React.useState(false)
    const router = useRouter()

    // Handle CMD+K / CTRL+K keyboard shortcut
    React.useEffect(() => {
        const down = (e: KeyboardEvent) => {
            if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                setOpen((open) => !open)
            }
        }

        document.addEventListener("keydown", down)
        return () => document.removeEventListener("keydown", down)
    }, [])

    // Handle search
    React.useEffect(() => {
        const performSearch = async () => {
            if (!query.trim()) {
                setResults(items)
                return
            }

            if (onSearch) {
                setLoading(true)
                try {
                    const searchResults = await onSearch(query)
                    setResults(searchResults)
                } catch (error) {
                    console.error("Search error:", error)
                    setResults([])
                } finally {
                    setLoading(false)
                }
            } else {
                // Client-side filtering
                const filtered = items.filter((item) => {
                    const searchText = `${item.title} ${item.description || ""} ${item.category || ""}`.toLowerCase()
                    return searchText.includes(query.toLowerCase())
                })
                setResults(filtered)
            }
        }

        const debounce = setTimeout(performSearch, 150)
        return () => clearTimeout(debounce)
    }, [query, items, onSearch])

    const handleSelect = (item: SearchItem) => {
        setOpen(false)
        setQuery("")
        if (onSelect) {
            onSelect(item)
        } else {
            router.push(item.url)
        }
    }

    // Group results by category
    const groupedResults = React.useMemo(() => {
        if (!showCategories) return { "": results }

        return results.reduce((acc, item) => {
            const category = item.category || "Other"
            if (!acc[category]) acc[category] = []
            acc[category].push(item)
            return acc
        }, {} as Record<string, SearchItem[]>)
    }, [results, showCategories])

    return (
        <>
            {showTrigger && (
                <button
                    data-sidebar="menu-button"
                    onClick={() => setOpen(true)}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground rounded-md transition-colors border border-border bg-transparent group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:!size-7"
                >
                    <Search className="h-4 w-4 shrink-0 text-muted-foreground group-data-[collapsible=icon]:!m-0" />
                    <span className="flex-1 text-left min-w-0 truncate">{placeholder}</span>
                    <kbd className="pointer-events-none hidden h-5 select-none items-center rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium text-foreground opacity-100 sm:flex dark:bg-muted dark:text-foreground dark:border-border group-data-[collapsible=icon]:!hidden">
                        <span className="text-xs">⌘</span>K
                    </kbd>
                </button>
            )}

            {/* Search Dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="p-0 gap-0 max-w-2xl" hideCloseButton>
                    <DialogHeader className="px-4 pt-4 pb-0">
                        <DialogTitle className="sr-only">Search</DialogTitle>
                        <DialogDescription className="sr-only">
                            Search for news, settings, and other resources.
                        </DialogDescription>
                        <div className="flex items-center gap-2 border-b pb-3">
                            <Search className="h-4 w-4 text-muted-foreground" />
                            <Input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={placeholder}
                                className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-8 px-0"
                                autoFocus
                            />
                            {query && (
                                <button
                                    onClick={() => setQuery("")}
                                    className="text-muted-foreground hover:text-foreground"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </DialogHeader>

                    <ScrollArea className="max-h-[400px] px-2 py-2">
                        {loading ? (
                            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                                Searching...
                            </div>
                        ) : results.length === 0 ? (
                            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                                {emptyMessage}
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {Object.entries(groupedResults).map(([category, categoryItems]) => (
                                    <div key={category}>
                                        {showCategories && category && (
                                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                                                {category}
                                            </div>
                                        )}
                                        <div className="space-y-1">
                                            {categoryItems.map((item) => (
                                                <button
                                                    key={item.id}
                                                    onClick={() => handleSelect(item)}
                                                    className={cn(
                                                        "flex items-start gap-3 w-full px-3 py-2.5 text-left rounded-md transition-colors",
                                                        "hover:bg-accent hover:text-accent-foreground",
                                                        "focus:bg-accent focus:text-accent-foreground focus:outline-none"
                                                    )}
                                                >
                                                    {item.icon && (
                                                        <div className="mt-0.5 text-muted-foreground">
                                                            {item.icon}
                                                        </div>
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-medium text-sm">{item.title}</div>
                                                        {item.description && (
                                                            <div className="text-xs text-muted-foreground line-clamp-1">
                                                                {item.description}
                                                            </div>
                                                        )}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </ScrollArea>

                    <div className="border-t px-4 py-2 text-xs text-muted-foreground">
                        <div className="flex items-center justify-between">
                            <span>Navigate with arrow keys</span>
                            <div className="flex items-center gap-2">
                                <kbd className="px-1.5 py-0.5 rounded bg-muted font-mono">↵</kbd>
                                <span>to select</span>
                                <kbd className="px-1.5 py-0.5 rounded bg-muted font-mono">esc</kbd>
                                <span>to close</span>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    )
}
